'use client'

import {
  getCoreRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type PaginationState,
  type RowData,
  type RowModel,
  type Updater,
  useReactTable,
} from '@tanstack/react-table'
import {
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useMemo,
} from 'react'

import { DATA_TABLE_ITEMS_PER_PAGE } from '@/utils/constants'
import { getPaginationPages } from '@/utils/getPaginationPages'

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../pagination'

import { DataCardNotFound } from './notFound'
import { DataCardSkeleton } from './skeleton'

type DataCardProps<TData extends RowData> = {
  data: TData[]
  pageCount: number
  resultsCount: number
  pagination: {
    currentPage: number
    onPageChange: (page: number) => void
  }
  renderContainerRow: (children: PropsWithChildren) => ReactNode
  renderRow: (row: TData) => ReactNode
  loading?: boolean
  emptyDataContainer?: ReactNode
}

const DataCardBody = <TData extends RowData>({
  loading,
  rowModel,
  emptyDataContainer,
  renderRow,
}: Pick<
  DataCardProps<TData>,
  'loading' | 'emptyDataContainer' | 'renderRow'
> & {
  rowModel: RowModel<TData>
}) => {
  if (loading) {
    return <DataCardSkeleton />
  }

  if (!rowModel.rows.length) {
    return emptyDataContainer ?? <DataCardNotFound />
  }

  return rowModel.rows.map((row) => renderRow(row.original))
}

const DataCard = <TData extends RowData>({
  data,
  pageCount,
  resultsCount,
  pagination,
  loading,
  emptyDataContainer,
  renderRow,
  renderContainerRow,
}: DataCardProps<TData>) => {
  const { currentPage, onPageChange } = pagination

  const onPaginationChange: OnChangeFn<PaginationState> = useCallback(
    (updaterOrValue: Updater<PaginationState>) => {
      const page =
        typeof updaterOrValue === 'function'
          ? updaterOrValue({
              pageIndex: currentPage - 1,
              pageSize: DATA_TABLE_ITEMS_PER_PAGE,
            }).pageIndex
          : updaterOrValue.pageIndex

      onPageChange(page + 1)
    },
    [currentPage, onPageChange],
  )

  const table = useReactTable({
    columns: [],
    data,
    pageCount,
    state: {
      pagination: {
        pageIndex: currentPage - 1,
        pageSize: DATA_TABLE_ITEMS_PER_PAGE,
      },
    },
    manualPagination: true,
    onPaginationChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  const page = table.getState().pagination.pageIndex + 1
  const paginationPages = useMemo(
    () => getPaginationPages(page, pageCount),
    [page, pageCount],
  )

  return (
    <div className="flex flex-col gap-4">
      {renderContainerRow({
        children: (
          <DataCardBody
            renderRow={renderRow}
            rowModel={table.getRowModel()}
            emptyDataContainer={emptyDataContainer}
            loading={loading}
          />
        ),
      })}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 text-sm text-system-text-secondary">
          <span>{resultsCount}</span> <span>resultados</span>
        </div>
        <Pagination>
          <PaginationContent>
            <PaginationItem
              isDisabled={!table.getCanPreviousPage()}
              onClick={table.previousPage}
            >
              <PaginationPrevious />
            </PaginationItem>

            {paginationPages.map((item, i) =>
              item === 'ellipsis' ? (
                <PaginationItem key={`ellipsis-${i}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={item} onClick={() => onPageChange(item)}>
                  <PaginationLink isActive={page === item}>
                    {item}
                  </PaginationLink>
                </PaginationItem>
              ),
            )}

            <PaginationItem
              isDisabled={!table.getCanNextPage()}
              onClick={table.nextPage}
            >
              <PaginationNext />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </div>
  )
}
DataCard.displayName = 'DataCard'

export { DataCard }
